import { useEffect, useState, type ReactNode } from 'react'
import { config } from './config'
import { cleanDesc } from './PredictionControls'
import type { EditionOption, NewBlockSpec, PackageOption } from './domain'

export function AddBlockPanel({ modelCodes, nmcMode, present, onAdd }: {
  modelCodes: string[]
  nmcMode: boolean
  present: Set<string>
  onAdd: (spec: NewBlockSpec) => void
}) {
  const [open, setOpen] = useState(false)
  const [pkgs, setPkgs] = useState<PackageOption[] | null>(null)
  const [eds, setEds] = useState<EditionOption[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])   // legacy multi-package
  const [edition, setEdition] = useState('')               // NMC single edition (nmc_model_code)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    if (!open) return
    const body = JSON.stringify({ model_codes: modelCodes })
    const headers = { 'Content-Type': 'application/json' }
    if (!eds) {
      fetch(`${config.apiUrl}/api/editions`, { method: 'POST', headers, body })
        .then(r => r.json()).then(d => setEds(d.editions ?? []))
        .catch(() => setErr('Could not load editions'))
    }
    if (!pkgs) {
      fetch(`${config.apiUrl}/api/packages`, { method: 'POST', headers, body })
        .then(r => r.json()).then(d => setPkgs(d.packages ?? []))
        .catch(() => setErr('Could not load packages'))
    }
  }, [open])

  const all = pkgs ?? []
  const sigOf = (codes: string[]) => [...codes].sort().join('|')

  // A block from an NMC edition — the edition is the identity; no legacy package needed.
  function specFromEdition(e: EditionOption): NewBlockSpec {
    return {
      packages: [], nmc_model_code: e.nmc_model_code, nmc_edition_code: e.edition_code,
      nmc_edition_name: e.edition_name, nmc_extra_packages: [], rows: [], weight_share: 0,
    }
  }

  // Legacy multi-package block: the first (sorted) selected package that maps to an
  // edition defines it; the rest are extras.
  function specFrom(codes: string[]): NewBlockSpec {
    const sorted = [...codes].sort()
    const ed = sorted.map(c => all.find(p => p.code === c)).find(p => p?.nmc_model_code) ?? null
    return {
      packages: sorted,
      nmc_model_code: ed?.nmc_model_code ?? null,
      nmc_edition_code: ed?.nmc_edition_code ?? null,
      nmc_edition_name: ed?.nmc_edition_name ?? null,
      nmc_extra_packages: ed ? sorted.filter(c => c !== ed.code) : [],
      rows: [], weight_share: 0,
    }
  }

  // Seed the block with the edition's typical rows + demand share so it holds cars and
  // auto-balances, then add it.
  async function addWith(base: NewBlockSpec) {
    setAdding(true)
    try {
      const res = await fetch(`${config.apiUrl}/api/edition-configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model_codes: modelCodes, nmc_model_code: base.nmc_model_code, packages: base.packages }),
      })
      const d = await res.json()
      onAdd({ ...base, rows: d.rows ?? [], weight_share: d.weight_share ?? 0 })
    } catch {
      onAdd(base)
    } finally {
      setAdding(false)
    }
  }

  const pkgLabel = (p: PackageOption) => {
    const d = cleanDesc(p.code, p.name)
    const base = d ? `${p.code} · ${d}` : p.code
    return p.count ? `${base}  ·  ${p.count} order${p.count === 1 ? '' : 's'}` : `${base}  ·  new`
  }

  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        className="mt-4 border border-bmw-ocean bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] text-bmw-ocean hover:bg-bmw-ocean hover:text-white"
      >
        + Add block
      </button>
    )
  }

  const loaded = pkgs !== null && eds !== null
  const shell = (count: string, note: string, body: ReactNode) => (
    <div className="mt-4 border border-[#E5E7EC] bg-[#FAFBFC] p-3" style={{ borderTop: '2px solid #92A2BD' }}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.06em] text-[#444]">Add block</span>
        {loaded && <span className="text-[10px] text-[#999]">{count}</span>}
      </div>
      {err && <p className="text-xs text-black">⚠ {err}</p>}
      {!loaded && !err && <p className="text-xs text-[#999]">Loading…</p>}
      {loaded && body}
      {loaded && <p className="mt-2 text-[11px] text-[#999]">{note}</p>}
    </div>
  )

  // NMC picks a single edition, straight from the model's mapped editions; fall back to
  // package selection for models with no NMC editions yet (F70/G20/G21/X1-18i).
  const editions = eds ?? []
  const nmcSingle = nmcMode && editions.length > 0

  // ── NMC: choose a single edition ──────────────────────────────────────────────
  if (nmcSingle) {
    const dup = !!edition && present.has(edition)
    return shell(`${editions.length} editions`,
      'Pick the edition — it is added with its typical configurations and demand share, ready to adjust.', (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border border-[#CFD3DB] bg-white px-2 py-1.5 text-xs font-mono min-w-0 flex-1"
            value={edition} onChange={e => setEdition(e.target.value)}
          >
            <option value="">Choose an edition…</option>
            {editions.map(e => (
              <option key={e.nmc_model_code} value={e.nmc_model_code}>
                {e.nmc_model_code} · {cleanDesc(e.nmc_model_code, e.edition_name) || e.nmc_model_code}
              </option>
            ))}
          </select>
          <button
            type="button" disabled={!edition || adding}
            onClick={() => {
              const e = editions.find(x => x.nmc_model_code === edition)
              if (e) { addWith(specFromEdition(e)); setEdition('') }
            }}
            className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white disabled:opacity-30"
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#999] hover:text-black">Done</button>
        </div>
        {dup && <p className="mt-1.5 text-[11px] text-[#8A5A00]">This edition already has a block — add options to tell them apart.</p>}
      </>
    ))
  }

  // ── Legacy: multi-select any packages (fully versatile) ─────────────────────────
  const q = query.trim().toLowerCase()
  const filtered = q
    ? all.filter(p => p.code.toLowerCase().includes(q) || (p.name ?? '').toLowerCase().includes(q))
    : all
  const dup = present.has(sigOf(selected))
  const toggle = (code: string) =>
    setSelected(s => s.includes(code) ? s.filter(c => c !== code) : [...s, code])
  const note = nmcMode
    ? 'No NMC editions for this model yet — pick package(s) directly; the block is seeded from matching orders.'
    : 'Pick the package(s) — the block is seeded from matching orders, ready to adjust.'
  return shell(`${selected.length} selected`, note, (
    <>
      <input
        type="text" value={query} onChange={e => setQuery(e.target.value)}
        placeholder="Search packages…"
        className="mb-2 w-full border border-[#CFD3DB] bg-white px-2 py-1.5 text-xs focus:border-bmw-ocean focus:outline-none"
      />
      <div className="max-h-52 divide-y divide-[#EEF0F4] overflow-auto border border-[#E5E7EC] bg-white">
        {filtered.length === 0
          ? <p className="px-2 py-3 text-[11px] text-[#999]">No matching packages.</p>
          : filtered.map(p => (
            <label key={p.code} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-[#F4F6F9]">
              <input type="checkbox" checked={selected.includes(p.code)} onChange={() => toggle(p.code)} />
              <span className="font-mono">{pkgLabel(p)}</span>
            </label>
          ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#666]">
          {selected.length ? selected.slice().sort().join(' + ') : 'No packages — base-car block'}
        </span>
        <button
          type="button" disabled={adding}
          onClick={() => { addWith(specFrom(selected)); setSelected([]); setQuery('') }}
          className="bg-bmw-ocean px-3 py-1.5 text-xs font-bold uppercase tracking-[0.06em] text-white disabled:opacity-30"
        >
          {adding ? 'Adding…' : 'Add block'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-[#999] hover:text-black">Done</button>
      </div>
      {dup && <p className="mt-1.5 text-[11px] text-[#8A5A00]">A block with those packages already exists — add options to tell them apart.</p>}
    </>
  ))
}
